import { useState } from 'react';

import {
  AngleRight,
  ChakraBox,
  ChakraFlex,
  ChakraSpacer,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  Group,
  Reset,
  User,
} from '@equip.health/ui';

import GroupClassEventKebabMenu from '~/components/maud/calendar/group-class/kebab-menus/GroupClassEventKebabMenu';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { shouldShowPatientProfileLink } from '~/lib/util/calendar.util';
import {
  isPastDateTime,
  isUserAttendingEvent,
} from '~/lib/util/classes/groupClasses.util';
import { getAgeFromDOB, getPreferredFullName } from '~/lib/util/patient.util';
import {
  ScheduleType,
  getRecurrenceDisplay,
  isAddNoteAllowed,
} from '~/lib/util/schedule/schedule.util';
import { Path } from '~/routes/constants';

import GroupClassEventSummary from '../../review/GroupClassEventSummary';
import AttendeeList from '../AttendeeList';
import ScheduleNote from '../ScheduleNote';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';

const { primary } = EquipUIFireflyV2Theme.colors;

const { h6, paragraph, paragraphBold } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'scheduler__review__appointment-summary';

const linkTextStyles = {
  ...paragraphBold,
  color: 'primary.100',
  cursor: 'pointer',
  lineHeight: '16.8px',
  variant: 'ghost',
};

interface ScheduleDayCardSummaryProps {
  isLinkDetail?: boolean;
  onCancelAppointment?: (externalId: string) => void;
  onFetchGroupClassEvents?: () => void;
  onNotesClicked?: (externalId: string) => void;
  onZoomClicked?: (externalId: string) => void;
  scheduleItem: ScheduleItemBase;
}

const ScheduleDayCardSummary = ({
  isLinkDetail = false,
  onCancelAppointment,
  onNotesClicked,
  onZoomClicked,
  scheduleItem,
}: ScheduleDayCardSummaryProps) => {
  const isPatientPath = useIsPatientPath();
  const [isHideParticipants, setIsHideParticipants] = useState<boolean>(true);

  const {
    currentUser: { userId: userExternalId },
    permissions,
  } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const handleViewPatientProfileClick = (): void => {
    window.open(
      `${window.location.origin}${Path.MAUD_PATIENTS}/${scheduleItem.patient.userId}`,
      '_blank',
      'noopener',
    );
  };

  const {
    attendees,
    duration,
    endDateTime,
    externalId: groupClassEventExternalId,
    frequency,
    groupClassExternalId,
    hosts,
    isNoteInDraftState,
    isNoteInSignedState,
    isShowZoomButton,
    noteAuthoredByProviderId,
    patient,
    startDateTime: startTime,
    title,
    typeOf,
    recurringUntilDateTime,
  } = scheduleItem;
  const hostIds =
    (hosts ?? []).map((host: ScheduleUserBase) => host.externalId) ?? [];
  const isHost = hostIds.includes(userExternalId);
  const isAttendee = isUserAttendingEvent(attendees, userExternalId);

  const frequencyText = getRecurrenceDisplay(
    startTime,
    frequency,
    recurringUntilDateTime,
  );

  const toGroupClassEventDetails = (
    event: ScheduleItemBase,
  ): GroupClassEventDetails => ({
    ...event,
    // this is not expected since this method is only called when type != appointment, but type improvement could be done
    groupClassExternalId: event.groupClassExternalId as string,
    attendees: event.attendees as GroupClassEventAttendee[],
    declinedUsers: [],
    endTime: null,
    frequency: event.frequency,
    groupClassEventExternalId,
    hosts: event.hosts as GroupClassEventAttendee[],
    iconName: null,
    startTime,
  });

  const { isDisabled: isAddNoteDisabled, isHidden: isAddNoteHidden } =
    isAddNoteAllowed(typeOf, isHost, isAttendee, isSchedulingAdmin);
  const isNoteAuthor = noteAuthoredByProviderId === userExternalId;
  const isNotAuthorOfApptNote =
    typeOf === ScheduleType.APPOINTMENT && isNoteInDraftState && !isNoteAuthor;

  /**
    Only show the note button if
      - There is an existing note linked to this event OR the logged in user is allowed to create a note
        AND
      - Not in Patient chart
  */
  const isShowNoteButton =
    (isNoteInSignedState || !isAddNoteHidden) &&
    !(isPatientPath && groupClassExternalId);

  const getAppointmentKebabMenu = () => {
    const kebabOptions = [];

    if (isHost || isSchedulingAdmin) {
      kebabOptions.push({
        label: 'Cancel appointment',
        leftIcon: FAIconName.trashRegular,
        leftIconColor: 'neutral.primary',
        onClick: () => {
          onCancelAppointment?.(groupClassEventExternalId);
        },
      });
    }

    return (
      kebabOptions.length > 0 && (
        <FireflyKebabMenu
          aria-label="appointment options"
          options={kebabOptions}
        />
      )
    );
  };

  const getEventKebabMenu = () => {
    return (
      <GroupClassEventKebabMenu
        groupClassEventExternalId={groupClassEventExternalId}
        groupClassExternalId={groupClassExternalId}
        hostIds={hostIds}
        isAttendee={isAttendee}
        isPastEvent={isPastDateTime(endDateTime)}
      />
    );
  };

  return (
    <ChakraFlex align="center" alignItems="start" width="100%">
      {typeOf === ScheduleType.APPOINTMENT ? (
        <ChakraFlex direction="column" width="80%">
          <ChakraText
            {...h6}
            id={`${TOP_LEVEL_ID}__appointment-type-and-duration`}
            paddingBottom="12px"
          >
            {`${title}, ${duration} minutes`}
          </ChakraText>
          {frequencyText && (
            <ChakraFlex align="center" paddingBottom="12px">
              <Reset fill={primary[100]} height="16px" width="16px" />
              <ChakraText
                {...paragraph}
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__recurrence`}
                lineHeight="16.8px"
                paddingLeft="12px"
              >
                {frequencyText}
              </ChakraText>
            </ChakraFlex>
          )}
          {patient && (
            <ChakraFlex align="center" paddingBottom="12px">
              <User fill={primary[100]} height="16px" width="16px" />
              <ChakraText
                {...paragraph}
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__patient`}
                lineHeight="16.8px"
                paddingLeft="12px"
              >
                {`${
                  patient.name ||
                  getPreferredFullName(
                    patient.chosenName,
                    patient.firstName,
                    patient.lastName,
                  )
                }, Age ${getAgeFromDOB(patient.dateOfBirth, 'yyyy-LL-dd')}`}
              </ChakraText>
              {shouldShowPatientProfileLink(patient?.userId) && (
                <ChakraText
                  backgroundColor="transparent"
                  {...linkTextStyles}
                  borderRadius="16px"
                  id={`${TOP_LEVEL_ID}__patient-profile-link`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewPatientProfileClick();
                  }}
                >
                  &nbsp;View profile
                </ChakraText>
              )}
            </ChakraFlex>
          )}
          <ChakraFlex align="center" paddingBottom="5px">
            <Group fill={primary[100]} height="16px" width="16px" />
            {attendees?.length > 0 ? (
              <>
                <ChakraText
                  {...paragraph}
                  color="neutral.secondary"
                  id={`${TOP_LEVEL_ID}__participant-count`}
                  lineHeight="16.8px"
                  paddingLeft="12px"
                >
                  {`${attendees?.length ?? 0} participant${
                    attendees?.length === 1 ? '' : 's'
                  }`}
                </ChakraText>
                <ChakraText
                  {...linkTextStyles}
                  id={`${TOP_LEVEL_ID}__show-hide-participants`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsHideParticipants(!isHideParticipants);
                  }}
                >
                  &nbsp;{isHideParticipants ? 'Show' : 'Hide'}
                </ChakraText>
              </>
            ) : (
              <ChakraText
                {...paragraph}
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__participant-count`}
                lineHeight="16.8px"
                paddingLeft="12px"
              >
                No attendees registered yet
              </ChakraText>
            )}
          </ChakraFlex>
          {!isHideParticipants && <AttendeeList attendees={attendees ?? []} />}
        </ChakraFlex>
      ) : (
        <GroupClassEventSummary
          groupClassEventDetails={toGroupClassEventDetails(scheduleItem)}
          isHostShow={false}
        />
      )}
      <ChakraSpacer />
      {isLinkDetail ? (
        <AngleRight fill={primary[100]} height="10px" width="15px" />
      ) : (
        <ChakraFlex
          alignItems="center"
          gap="12px"
          justifyContent="end"
          width="100%"
        >
          {isShowZoomButton && (
            <FireflyButton
              id={`${TOP_LEVEL_ID}__zoom-v2`}
              onClick={(e) => {
                e.stopPropagation();
                onZoomClicked?.(groupClassEventExternalId);
              }}
              size="small"
            >
              Join Zoom
            </FireflyButton>
          )}

          {isShowNoteButton && (
            <ScheduleNote
              isAddNote={!isNoteInSignedState && !isNoteInDraftState}
              isDisabled={
                isNotAuthorOfApptNote ||
                (isAddNoteDisabled && !isNoteInSignedState)
              }
              isNoteInDraftState={isNoteInDraftState}
              isShowTooltip={
                typeOf === ScheduleType.APPOINTMENT &&
                isAddNoteDisabled &&
                !isNoteInSignedState
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (isNoteInSignedState || !isAddNoteDisabled) {
                  onNotesClicked?.(groupClassEventExternalId);
                }
              }}
            />
          )}
          <ChakraBox
            id={`${TOP_LEVEL_ID}__kebab-menu-wrapper`}
            onClick={(e) => {
              e.stopPropagation();
            }}
            paddingTop="6px"
          >
            {typeOf === ScheduleType.APPOINTMENT
              ? getAppointmentKebabMenu()
              : getEventKebabMenu()}
          </ChakraBox>
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default ScheduleDayCardSummary;
